import * as React from "react";

import { cn } from "@src/lib/utils/cn";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  inputClassName?: string;
}

/**
 * The shared text-field look: 36px tall, rounded-lg, a border-secondary
 * hairline on bg-primary, 14px text with a text-tertiary placeholder, and
 * Button's 3px brand-border focus ring. aria-invalid switches the border and
 * ring to status-error. Textarea and Select triggers should adopt it.
 */
export const inputFieldClassName =
  "flex h-9 self-stretch rounded-lg border border-border-secondary bg-bg-primary px-3 py-1 font-sans-v4 text-s-v4 font-normal-v4 text-text-primary transition-[color,border-color,box-shadow] placeholder:text-text-tertiary file:border-0 file:bg-transparent file:font-medium-v4 file:text-text-primary focus-visible:border-brand focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-status-error-solid aria-invalid:focus-visible:border-status-error-solid aria-invalid:focus-visible:ring-status-error-border";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, label, description, inputClassName, id, ...props },
    ref,
  ) => {
    const generatedId = React.useId();
    const inputId = id || (label || description ? generatedId : undefined);
    const descriptionId = description
      ? `${generatedId}-description`
      : undefined;
    const invalid =
      props["aria-invalid"] === true || props["aria-invalid"] === "true";

    return (
      <div
        className={cn(
          "flex flex-col w-full gap-1.5",
          props.disabled && "cursor-not-allowed",
          className,
        )}
      >
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "font-sans-v4 text-xs-v4 font-medium-v4 text-text-primary",
              props.disabled && "opacity-50",
            )}
          >
            {label}
          </label>
        )}
        <input
          type={type}
          id={inputId}
          className={cn(inputFieldClassName, inputClassName)}
          ref={ref}
          {...props}
          aria-describedby={
            [props["aria-describedby"], descriptionId]
              .filter(Boolean)
              .join(" ") || undefined
          }
        />
        {description && (
          <p
            id={descriptionId}
            className={cn(
              "font-sans-v4 text-2xs-v4 font-normal-v4 text-text-secondary",
              invalid && "text-status-error-text",
              props.disabled && "opacity-50",
            )}
          >
            {description}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
